import { LoggedInResponse } from 'src/hooks/useLogin/types'
import i18n from 'src/i18n'
import { API_ENDPOINTS } from 'src/routes/routes'
import { getFromStorage, saveToStorage } from 'src/shared/helpers/sessionStorageHelpers'
import { getCookie } from 'src/utils'

import { InvalidAccessTokenError, ServerErrors } from '../../constants'

import axios, { AxiosError, AxiosRequestConfig, isCancel } from 'axios'

interface ValidationErrorDetail {
    type: string
    msg: string
    loc?: string[]
}

const abortControllers: Record<string, AbortController> = {}

// Refresh a new token when the current token is expired
axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        if (error.response?.status === 401 && Object.values(error.response?.data).includes('Unauthorized')) {
            if (error.config.url.includes(API_ENDPOINTS.REFRESH_TOKEN)) {
                throw new InvalidAccessTokenError()
            }

            const { accessToken, refreshToken } = getFromStorage('user')
            if (refreshToken) {
                try {
                    const result = await fetchRequest<LoggedInResponse>(API_ENDPOINTS.REFRESH_TOKEN, {
                        method: 'POST',
                        data: JSON.stringify({ accessToken, refreshToken }),
                    })

                    saveToStorage('user', result)
                    error.config.headers.Authorization = `Bearer ${result.accessToken}`
                    return axios(error.config)
                } catch (refreshError) {
                    if (refreshError instanceof InvalidAccessTokenError) {
                        throw refreshError
                    }

                    for (let index = 0; index < 50; index++) {
                        //Delay 1000ms before retrying
                        await new Promise((resolve) => setTimeout(resolve, 100))

                        const newToken = getFromStorage('user').accessToken
                        if (!!newToken && accessToken !== newToken) {
                            const currentToken = getFromStorage('user').accessToken
                            error.config.headers.Authorization = `Bearer ${currentToken}`
                            return axios(error.config)
                        }
                    }
                }
            }
        }

        return Promise.reject(error)
    },
)

const fetchRequest = async <T extends {}>(
    url: string,
    options: Partial<AxiosRequestConfig> = {},
    uploadRequest?: boolean,
): Promise<T> => {
    const { accessToken } = getFromStorage('user')

    // backend framework expects X-CSRFToken in the headers instead of cookies
    const CSRFToken = getCookie('csrftoken')
    const appendCSRFTOKEN = options?.method === 'POST' && CSRFToken

    const appendAccessToken = !!accessToken && url !== API_ENDPOINTS.REFRESH_TOKEN

    const bodyContainsFormData = isFormData(options.data) || Object.values(options.data ?? {}).some(isFormData)
    const JSONContentType = options.data && !bodyContainsFormData

    const abortControllerKey = [url, options?.method || 'GET'].join('-')
    // Don't cancel upload request, as there can be multiple requests at the same time
    if (!uploadRequest && abortControllers[abortControllerKey]) {
        abortControllers[abortControllerKey].abort()
        delete abortControllers[abortControllerKey]
    }

    // Initialize a new AbortController to cancel the request if needed
    // The abortcontroller is connected to the url to prevent cancelling other requests
    abortControllers[abortControllerKey] = new AbortController()

    const requestOptions: AxiosRequestConfig = {
        ...options,
        headers: {
            'Accept-Language': i18n.language,
            ...(appendCSRFTOKEN ? { 'X-CSRFToken': CSRFToken } : {}),
            ...(JSONContentType ? { 'Content-Type': 'application/json' } : {}),
            ...(appendAccessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            ...options.headers,
        },
        signal: abortControllers[abortControllerKey].signal,
        paramsSerializer: { ...options.paramsSerializer, indexes: null },
    }

    // EVALUATE THIS AGAIN WHEN REACTIVATION OF TESTS IF NEEDED
    // AS THE VITE CONFIG IS CHANGED AT 24/10/2025
    // const envMode = import.meta.env.MODE
    // const runningVitest = envMode === 'test'
    // const runningProduction = envMode === 'production'
    // const runningPlaywright = envMode === 'playwright'
    // const appendUrl =
    //      !runningProduction && (runningPlaywright || (!runningVitest && import.meta.env.STORYBOOK_RUNNING !== 'true'))

    // Vite proxy only works with a prefix, so if we are not running unit tests or storybook, we need to append the prefix
    //let finalUrl = appendUrl ? `${import.meta.env.VITE_PROXY_PREFIX}${url}` : url

    return axios(url, requestOptions)
        .then((response) => {
            if (response) {
                delete abortControllers[abortControllerKey]

                if (response.status === 204) {
                    return null
                }

                if (response.status >= 200 && response.status < 300) {
                    return response.data as T
                }
            }

            throw new Error('An error occurred. Fetch request not executed.')
        })
        .catch((err) => {
            if (isCancel(err)) {
                throw err
            }

            delete abortControllers[abortControllerKey]

            if (err.response?.data.hasOwnProperty('key') && err.response?.data.hasOwnProperty('message')) {
                throw new ServerErrors([{ key: err.response.data.key, message: err.response.data.message }], err)
            }

            if (err.response?.data.hasOwnProperty('errors')) {
                throw new ServerErrors(err.response.data.errors, err)
            }

            if (Array.isArray(err.response?.data?.detail) && err.response.data.detail.length > 0) {
                const details = err.response.data.detail as ValidationErrorDetail[]
                const errors = details.map(({ type, msg, loc }) => ({
                    key: type,
                    message: msg,
                    fieldName: loc?.length > 2 ? loc.slice(2) : undefined,
                }))
                throw new ServerErrors(errors, err)
            }

            if (err instanceof InvalidAccessTokenError) {
                throw err
            }

            if (err.response?.data?.message) {
                throw new ServerErrors([{ key: '', message: err.response.data.message }], err)
            }

            throw new Error(err.message ?? 'An error occurred. Fetch request not executed.')
        })
}

const isFormData = (value: unknown): value is FormData => value instanceof FormData

export default fetchRequest
